import React from "react";
import { Box, Paper, Typography, TextField, Button, Chip, Tooltip } from "@mui/material";
import api from "../Auth/Api";
import globals from "../Globals";
import { navigate } from "../Layout/Router";
import { PageContainer, SectionHeader, SelectField, DataTable, formatDate } from "../Layout/Common";
import { FIXED_TABLE } from "./Localization/loc";

// The Social tab's match feed (Evil Tower, service `social`, wire 0.25): the top validated arcane matches of the last
// 24 hours, 25 per chapter, each player's best once per chapter, mixed by chapter percentile - rebuilt every hour by the
// scheduler (social.feed). Moderation: hide a match for good, reset a player's name (the popup asks again, off the feed
// until confirmed), private mode (the next rebuild).
const CHAPTERS = ["plains", "mountain", "forest", "tundra"];
const social = (description, parts, options) => api.group("social")(description, parts, options);
const canEdit = () => {
    if (globals.loginData?.titles?.isMaster) return true;
    const roles = globals.loginData?.titles?.roles?.[globals.selectedTitle] ?? [];
    return roles.includes("social");
};

export default class SocialPage extends React.Component {
    state = {
        branch: localStorage.getItem("social.branch") || "Production", feed: null, loading: false, busy: false,
        player: "", playerInfo: null,
    };

    componentDidMount() { this.load(); }

    load = () => {
        const { branch } = this.state;
        localStorage.setItem("social.branch", branch);
        this.setState({ loading: true });
        social("Loading the feed", ["feed", branch], { silent: true })
            .then(r => { if (r) this.setState({ feed: r }); })
            .catch(() => {})
            .finally(() => this.setState({ loading: false }));
    }

    rebuild = () => {
        this.setState({ busy: true });
        social("Rebuilding the feed", ["rebuild", this.state.branch], { successMessage: "Feed rebuilt" })
            .then(() => this.load())
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    }

    hide = (e) => {
        if (!window.confirm(`Hide match ${e.matchId} (${e.name}, ${e.world} wave ${e.waves}) from the feed for good?`)) return;
        this.setState({ busy: true });
        social("Hiding the match", ["hide", e.playerId, e.matchId], { successMessage: "Match hidden" })
            .then(() => this.load())
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    }

    lookup = () => {
        const p = this.state.player.trim();
        if (!p) return;
        social("Loading the player", ["player", p], { silent: true })
            .then(r => { if (r && r.playerId) this.setState({ playerInfo: r }); })
            .catch(() => {});
    }

    playerAction = (parts, description, success) => {
        this.setState({ busy: true });
        social(description, parts, { successMessage: success })
            .then(() => { this.lookup(); this.load(); })
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    }

    playerLink = (playerId) => (
        <Typography component="span" variant="body2" sx={{ color: "primary.main", cursor: "pointer", fontFamily: "monospace" }}
                    onClick={() => navigate({ title: globals.selectedTitle, page: "home", sub: "players", deep: [playerId] })}>
            {playerId.slice(-8)}
        </Typography>
    );

    matchLink = (e) => (
        <Typography component="span" variant="body2" sx={{ color: "primary.main", cursor: "pointer", fontFamily: "monospace" }}
                    onClick={() => navigate({ title: globals.selectedTitle, page: "matches", deep: [e.playerId, e.matchId] })}>
            {e.matchId.slice(0, 10)}
        </Typography>
    );

    render() {
        const { branch, feed, loading, busy, player, playerInfo } = this.state;
        const edit = canEdit();
        const columns = [
            { id: "rank", label: "#", width: "5%", align: "right" },
            { id: "name", label: "Name", width: "16%", render: e => <span style={{ wordBreak: "break-word" }}>{e.name}</span> },
            { id: "player", label: "Player", width: "9%", render: e => this.playerLink(e.playerId) },
            { id: "world", label: "Chapter", width: "10%" },
            { id: "waves", label: "Waves", width: "7%", align: "right" },
            { id: "score", label: "Percentile", width: "9%", align: "right",
              render: e => <Tooltip title="the share of the chapter's validated arcane matches (7 days) this run beat - what the mix is sorted by"><span>{(e.score * 100).toFixed(1)}%</span></Tooltip> },
            { id: "endedAt", label: "Ended", width: "15%", render: e => <span>{formatDate(e.endedAt)} <Typography component="span" variant="caption" color={e.expired ? "error" : "text.secondary"}>{e.expired ? "expired" : `${e.ageHours} h ago`}</Typography></span> },
            { id: "match", label: "Match", width: "11%", render: e => this.matchLink(e) },
            { id: "version", label: "Version", width: "8%", render: e => `${e.recVersion} · sim ${e.dbPatch}` },
            { id: "actions", label: "", width: "10%", align: "right", render: e => edit ? <Button size="small" color="error" onClick={() => this.hide(e)} disabled={busy}>Hide</Button> : "" },
        ];
        return (
            <PageContainer>
                <SectionHeader title="Match feed">
                    <Typography variant="body2" color="text.secondary">
                        The top validated arcane matches of the last 24 hours: 25 per chapter, each player's best once per chapter, only players who confirmed a name and are not in private mode.
                        Mixed by chapter percentile. Rebuilt every hour; an entry leaves 24 hours after its match ended.
                    </Typography>
                </SectionHeader>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                        <SelectField label="Branch" size="small" minWidth={150} value={branch} options={globals.branches} onChange={v => this.setState({ branch: v }, this.load)} />
                        <Button variant="outlined" onClick={this.load} disabled={loading}>{loading ? "Loading…" : "Reload"}</Button>
                        {edit && <Button variant="contained" onClick={this.rebuild} disabled={busy}>Rebuild now</Button>}
                        {feed && feed.version > 0 && (
                            <Typography variant="body2" color="text.secondary">
                                version {feed.version} · built {formatDate(feed.builtAt)} · {feed.live} live of {feed.entries.length}
                            </Typography>
                        )}
                    </Box>
                    {feed && feed.version > 0 && (
                        <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                            {CHAPTERS.map(c => (
                                <Tooltip key={c} title={`${feed.candidates?.[c] ?? 0} player(s) with a validated arcane match in the last 24 h · ${feed.population?.[c] ?? 0} validated arcane matches in 7 days (the percentile population)`}>
                                    <Chip size="small" label={`${c}: ${feed.entries.filter(e => e.world === c).length} / ${feed.candidates?.[c] ?? 0} · pop ${feed.population?.[c] ?? 0}`} />
                                </Tooltip>
                            ))}
                        </Box>
                    )}
                </Paper>
                {feed && (feed.entries?.length ?? 0) > 0
                    ? <DataTable sx={FIXED_TABLE} columns={columns} rows={feed.entries} getRowKey={e => e.matchId} />
                    : <Paper sx={{ p: 2, mb: 2 }}><Typography variant="body2" color="text.secondary">{loading ? "Loading…" : "The feed is empty on this branch."}</Typography></Paper>}

                <Paper sx={{ p: 2, mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Player</Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                        <TextField label="Player (link or id)" size="small" value={player} onChange={e => this.setState({ player: e.target.value })} onKeyDown={e => { if (e.key === "Enter") this.lookup(); }} sx={{ width: 260 }} />
                        <Button variant="outlined" onClick={this.lookup}>Look up</Button>
                    </Box>
                    {playerInfo && (
                        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Typography variant="body2"><b>{playerInfo.name}</b> · {this.playerLink(playerInfo.playerId)} · {playerInfo.branch}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                name {playerInfo.nameConfirmed ? `confirmed ${formatDate(playerInfo.nameAt)}` : "never confirmed (the popup asks; off the feed)"} ·
                                {playerInfo.privateMode ? " private mode" : " public"} · {playerInfo.eligible ? "eligible for the feed" : "not eligible"} ·
                                {` ${playerInfo.recordingDownloads?.count ?? 0} recording download(s) this hour`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                on the feed: {(playerInfo.onFeed ?? []).length ? playerInfo.onFeed.map(e => `#${e.rank} ${e.world} w${e.waves}`).join(", ") : "—"}
                            </Typography>
                            {edit && (
                                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                    <Button size="small" color="warning" disabled={busy || !playerInfo.nameConfirmed}
                                            onClick={() => window.confirm(`Reset ${playerInfo.name}'s name? The Social tab asks for a name again and the player leaves the feed until one is confirmed.`)
                                                && this.playerAction(["name-reset", playerInfo.playerId], "Resetting the name", "Name reset")}>Reset name</Button>
                                    <Button size="small" disabled={busy}
                                            onClick={() => this.playerAction(["private", playerInfo.playerId, playerInfo.privateMode ? "off" : "on"], "Switching private mode", "Private mode switched (next rebuild)")}>
                                        {playerInfo.privateMode ? "Private mode off" : "Private mode on"}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}
                </Paper>
            </PageContainer>
        );
    }
}
